/**
 * Pure planning for a chunked video upload. No DOM, no network — so the parts
 * arithmetic, the resume diff and the retry ladder can be tested, which matters
 * more here than usual: an off-by-one in the part manifest produces a video
 * that uploads "successfully" and then plays for two seconds.
 */

export type UploadPart = {
  partNumber: number;
  /** Byte offset into the file — `file.slice(start, end)`. */
  start: number;
  end: number;
};

/**
 * Split a file into parts.
 *
 * R2 requires every part except the LAST to be exactly the same size, so this
 * is a straight division with a short tail — never a "balance the chunks"
 * scheme. Part numbers are 1-based, as S3 requires.
 */
export function planParts(sizeBytes: number, partSize: number): UploadPart[] {
  if (sizeBytes <= 0 || partSize <= 0) return [];
  const parts: UploadPart[] = [];
  let partNumber = 1;
  for (let start = 0; start < sizeBytes; start += partSize) {
    parts.push({ partNumber, start, end: Math.min(start + partSize, sizeBytes) });
    partNumber += 1;
  }
  return parts;
}

/** How many parts a file of this size needs — the server's own `partCount`. */
export function countParts(sizeBytes: number, partSize: number): number {
  return partSize > 0 ? Math.ceil(sizeBytes / partSize) : 0;
}

/**
 * Which parts a resumed upload still has to send.
 *
 * `uploaded` comes from the SERVER asking storage what landed, not from
 * anything the browser remembered, so a reload (or a different device) resumes
 * correctly. Order is preserved so the upload still walks the file forwards.
 */
export function remainingParts(all: UploadPart[], uploaded: number[]): UploadPart[] {
  const done = new Set(uploaded);
  return all.filter((p) => !done.has(p.partNumber));
}

/**
 * Can this file resume that upload?
 *
 * Size is the fingerprint. It is not a checksum and does not pretend to be: the
 * cost of being wrong is a corrupt video the author will notice immediately,
 * while hashing a gigabyte in the browser to re-open an upload they started ten
 * minutes ago is a worse trade. A different file is almost always a different
 * length.
 */
export function canResume(fileSize: number, stagedBytes: number | null | undefined): boolean {
  return stagedBytes != null && stagedBytes === fileSize;
}

/** Backoff before retry N (1-based). Fixed ladder — long enough to outlast a
 *  handover between cell towers, short enough not to look frozen. */
export const RETRY_DELAYS_MS = [1000, 3000, 8000];

export function retryDelayMs(attempt: number): number | null {
  return RETRY_DELAYS_MS[attempt - 1] ?? null;
}

/**
 * Upload progress as a percentage.
 *
 * Bytes, not parts: with 8 MiB parts a 20 MB file has three of them, and a
 * parts-based bar would jump 0 → 33 → 66 → 100 and sit still in between.
 */
export function progressPct(uploadedBytes: number, totalBytes: number): number {
  if (totalBytes <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100)));
}

/** Human size for the cap copy — the author needs MB, not bytes. */
export function formatMb(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb % 1 === 0 ? gb : gb.toFixed(1)} GB`;
  }
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}
